<template>
    <div class="container">
        <div class="intro-box">
                    <h2 v-html="productListWelcomeTitle"></h2>
                    <p v-html="productListIntro"></p>
                </div>

        <h1 class="page-title" v-html="productListTitle"></h1>

        <div v-if="availableTags.length" class="filter-panel">
            <div class="filter-header">
                <h3>Nach Tags filtern</h3>
                <button
                    v-if="selectedTagIds.length"
                    type="button"
                    class="clear-filter-btn"
                    @click="clearTagFilters"
                >
                    Filter zuruecksetzen
                </button>
            </div>

            <div class="logic-toggle" v-if="selectedTagIds.length > 1">
                <label>
                    <input type="radio" value="or" v-model="tagLogic" />
                    ODER (mindestens ein Tag passt)
                </label>
                <label>
                    <input type="radio" value="and" v-model="tagLogic" />
                    UND (alle gewaehlten Tags muessen passen)
                </label>
            </div>

            <div class="tag-filter-list">
                <label v-for="tag in availableTags" :key="tag.id" class="tag-filter-item">
                    <input
                        type="checkbox"
                        :checked="selectedTagIds.includes(tag.id)"
                        @change="toggleTagSelection(tag.id)"
                    />
                    <span
                        class="tag-pill"
                        :style="{ backgroundColor: tag.color || '#3498db' }"
                        :title="tag.description || 'Keine Beschreibung hinterlegt.'"
                    >
                        {{ tag.name }}
                    </span>
                </label>
            </div>
        </div>

        <div v-if="loading" class="loading-message">
            <span v-html="loadingMessage"></span>
        </div>

        <div v-else-if="error" class="error-message">
            <span v-html="errorMessage"></span>: {{ error }}
        </div>
        
        <div v-else>
            <div v-if="!filteredProducts.length" class="empty-filter-message">
                Keine Produkte passen zu den ausgewaehlten Tags.
            </div>

            <div v-else class="product-grid">
            <div
                v-for="product in filteredProducts"
                :key="product.id"
                @click="goToDetail(product.id)"
                class="product-card"
            >
                <div class="image-wrapper">
                    <img 
                        :src="(product.images && product.images[0]) || product.image" 
                        onerror="this.onerror=null; this.src='https://placehold.co/600x400/cccccc/333333?text=Bild+Fehlt'" 
                        :alt="product.name" 
                        class="product-image"
                    >
                </div>

                <div class="card-content">
                    <div class="card-header">
                        <h2 class="product-name">{{ product.name }}</h2>
                        <span class="product-price">{{ formatCurrency(product.price) }}</span>
                    </div>
                    
                    <p class="product-description">{{ product.description }}</p>

                    <div v-if="product.tags && product.tags.length" class="card-tags">
                        <span
                            v-for="tag in product.tags"
                            :key="tag.id"
                            class="card-tag"
                            :style="{ backgroundColor: tag.color || '#3498db' }"
                            :title="tag.description || 'Keine Beschreibung hinterlegt.'"
                        >
                            {{ tag.name }}
                        </span>
                    </div>
                    
                    <button class="details-btn">
                        <span v-html="detailsButton"></span>
                    </button>
                </div>
            </div>
        </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useConfig } from '../composables/useConfig.js';
import { getProducts } from '../services/dbApi';

const { config, loadConfig } = useConfig();

const products = ref([]);
const router = useRouter();
const loading = ref(true);
const error = ref(null);
const selectedTagIds = ref([]);
const tagLogic = ref('or');

const productListWelcomeTitle = computed(() => config?.productList?.welcomeTitle || 'Willkommen in der Schatzkammer');
const productListIntro = computed(() => config?.productList?.intro || 'Entdecken Sie handgegossene Dekostücke aus Raysin...');
const productListTitle = computed(() => config?.productList?.title || 'Unsere Produkte');
const detailsButton = computed(() => config?.productList?.detailsButton || 'Details ansehen');
const loadingMessage = computed(() => config?.productList?.loadingMessage || 'Produkte werden geladen...');
const errorMessage = computed(() => config?.productList?.errorMessage || 'Fehler beim Laden der Produkte');

const availableTags = computed(() => {
    const tagMap = new Map();
    for (const product of products.value) {
        if (!Array.isArray(product?.tags)) continue;
        for (const tag of product.tags) {
            if (!tag || tag.id === undefined || tag.id === null) continue;
            if (!tagMap.has(tag.id)) {
                tagMap.set(tag.id, tag);
            }
        }
    }
    return Array.from(tagMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
});

const filteredProducts = computed(() => {
    if (!selectedTagIds.value.length) {
        return products.value;
    }

    return products.value.filter(product => {
        const productTagIds = Array.isArray(product.tags) ? product.tags.map(tag => tag.id) : [];

        if (tagLogic.value === 'and') {
            return selectedTagIds.value.every(tagId => productTagIds.includes(tagId));
        }

        return selectedTagIds.value.some(tagId => productTagIds.includes(tagId));
    });
});

const loadProducts = async () => {
    try {
        loading.value = true;
        error.value = null;
        products.value = await getProducts();
    } catch (err) {
        console.error('Error loading products:', err);
        error.value = err.message;
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    loadConfig();
    loadProducts();
});

const goToDetail = (id) => {
    router.push({ name: 'ProductDetail', params: { id } });
};

const toggleTagSelection = (tagId) => {
    if (selectedTagIds.value.includes(tagId)) {
        selectedTagIds.value = selectedTagIds.value.filter(id => id !== tagId);
        return;
    }
    selectedTagIds.value = [...selectedTagIds.value, tagId];
};

const clearTagFilters = () => {
    selectedTagIds.value = [];
    tagLogic.value = 'or';
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};
</script>

<style scoped>
/* Container begrenzt die maximale Breite der Seite */
.container {
    max-width: 1100px;
    margin: 40px auto;
    padding: 0 20px;
    font-family: var(--body-font-family, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif);
}

/* Styling der Intro-Box */
.intro-box {
    background-color: var(--page-background-color, #f9f9f9);
    padding: 30px;
    border-radius: 8px;
    text-align: center;
    margin-bottom: 40px;
    border: 1px solid #eee;
}

.intro-box h2 {
    margin-top: 0;
    color: var(--theme-primary-color, #333);
}

.intro-box p {
    color: var(--body-text-color, #666);
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.6;
}

.page-title {
    border-bottom: 2px solid #eee;
    padding-bottom: 15px;
    margin-bottom: 30px;
    color: var(--theme-primary-color, #222);
}

.filter-panel {
    background: #f8f8f8;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;
}

.filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.filter-header h3 {
    margin: 0;
    color: var(--theme-primary-color, #333);
    font-size: 1rem;
}

.clear-filter-btn {
    border: 1px solid #d0d0d0;
    background: #fff;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 0.85rem;
    cursor: pointer;
}

.clear-filter-btn:hover {
    background: #f1f1f1;
}

.logic-toggle {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 12px;
    font-size: 0.92rem;
    color: var(--body-text-color, #555);
}

.logic-toggle label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tag-filter-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.tag-filter-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.tag-pill {
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
    border-radius: 999px;
    padding: 6px 10px;
}

.empty-filter-message {
    text-align: center;
    padding: 20px;
    border-radius: 6px;
    background: #fff8e1;
    color: #8a6d3b;
    margin-bottom: 20px;
}

/* Lade- und Fehlermeldungen */
.loading-message,
.error-message {
    padding: 20px;
    text-align: center;
    border-radius: 6px;
    margin: 20px 0;
}

.loading-message {
    background-color: #e3f2fd;
    color: #1976d2;
}

.error-message {
    background-color: #ffebee;
    color: #d32f2f;
}

/* --- RESPONSIVES GRID LAYOUT --- */
.product-grid {
    display: grid;
    grid-template-columns: 1fr; /* Mobil: 1 Spalte (alles untereinander) */
    gap: 30px; /* Abstand zwischen den Karten */
}

/* Media Query: Ab 768px (Tablet) schalten wir auf 2 Spalten */
@media (min-width: 768px) {
    .product-grid {
        grid-template-columns: 1fr 1fr; /* Zwei gleich große Spalten */
    }
}

/* Produkt Karte: Flexbox für vertikale Ausrichtung */
.product-card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden; /* Damit das Bild an den Ecken abgerundet bleibt */
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s; /* Animationen */
    display: flex;
    flex-direction: column; /* Elemente untereinander anordnen */
}

/* Hover-Effekt: Karte hebt sich leicht an */
.product-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.image-wrapper {
    height: 250px; /* Feste Höhe für Einheitlichkeit */
    background-color: #f4f4f4;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.product-image {
    width: 100%;
    height: 100%;
    object-fit: cover; /* Bild füllt den Bereich, schneidet Ränder ab falls nötig */
}

.card-content {
    padding: 20px;
    display: flex;
    flex-direction: column;
    flex-grow: 1; /* Füllt den restlichen Platz in der Karte */
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline; /* Text und Preis auf einer Linie */
    margin-bottom: 10px;
}

.product-name {
    font-size: 1.2rem;
    margin: 0;
    color: var(--theme-primary-color, #333);
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
}

.product-price {
    font-size: 1.2rem;
    color: var(--interactive-accent-color, #2e7d32);
    font-weight: bold;
    white-space: nowrap;
    margin-left: 10px;
}

.product-description {
    color: var(--body-text-color, #666);
    line-height: 1.5;
    margin-bottom: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 2,5em;
}

.card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;
}

.card-tag {
    color: #fff;
    font-size: 0.76rem;
    font-weight: 600;
    border-radius: 999px;
    padding: 4px 8px;
}

.details-btn {
    width: 100%;
    padding: 12px;
    background-color: var(--button-background-color, #3f51b5);
    color: var(--button-label-color, #ffffff);
    border: none;
    border-radius: 6px;
    font-weight: bold;
    cursor: pointer;
    transition: background 0.3s;
}

.details-btn:hover {
    background-color: var(--theme-on-primary-text-color, #303f9f);
}
</style>